import { useEffect, useMemo, useState } from 'react';
import { AutoComplete, Button, Modal, Popconfirm, Progress, Select, Space, Table, Upload, message } from 'antd';
import Title from 'antd/lib/typography/Title';
import { ColumnsType } from 'antd/lib/table';
import { DeleteOutlined, DownloadOutlined, FolderOpenOutlined, UploadOutlined } from '@ant-design/icons';
import { useAuth } from '../../Contexts/AuthContext';
import { TextService } from '../../Services/TextService';
import { DocumentService } from '../../Services/DocumentService';
import { WebApiDocumentsDtoCompanyDocumentDto } from '../../Repository/eventbookyapi';
import { ResponseError } from '../../Repository/eventbookyapi/runtime';
import { formatDateString } from '../../Helpers/DateHelpers';
import { downloadBlobFile, getFileNameFromContentDisposition } from '../../Helpers/FileDownloadHelper';
import LayoutLeftMenu from '../../Components/Layout/LayoutLeftMenu';
import LeftMenuSettings from '../Account/LeftSettings';
import { SubscriptionLevel } from '../../Types/SubscriptionLevel';

const MAX_FILE_SIZE_BYTES = 25 * 1024 * 1024;
const ALLOWED_CONTENT_TYPES = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'image/jpeg',
    'image/png',
];

// Sentinel used only in the folder-filter dropdown to mean "documents with no folder".
const NO_FOLDER = '__none__';

const formatBytes = (bytes: number) => {
    if (!bytes) {
        return '0 MB';
    }
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
};

type FolderModalState =
    | { open: false }
    | { open: true; mode: 'upload'; file: File; value: string }
    | { open: true; mode: 'move'; docId: number; value: string };

const Documents = () => {
    const { user } = useAuth();
    const GetText = TextService.GetText;

    const [documents, setDocuments] = useState<WebApiDocumentsDtoCompanyDocumentDto[]>([]);
    const [usedBytes, setUsedBytes] = useState(0);
    const [limitBytes, setLimitBytes] = useState(0);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [folderFilter, setFolderFilter] = useState<string | undefined>(undefined);
    const [folderModal, setFolderModal] = useState<FolderModalState>({ open: false });

    useEffect(() => {
        loadDocuments();
    }, []);

    const loadDocuments = async () => {
        setLoading(true);
        try {
            const result = await DocumentService.GetList();
            setDocuments(result.documents || []);
            setUsedBytes(result.usedBytes || 0);
            setLimitBytes(result.limitBytes || 0);
        } catch {
            message.error(GetText('Documents_FailedToLoad') || 'Kunde inte hämta dokument.');
        }
        setLoading(false);
    };

    const existingFolders = useMemo(
        () => Array.from(new Set(documents.map(d => d.folder).filter((f): f is string => !!f))).sort((a, b) => a.localeCompare(b)),
        [documents],
    );

    const folderOptions = useMemo(() => existingFolders.map(f => ({ value: f })), [existingFolders]);

    const filteredDocuments = useMemo(() => {
        if (folderFilter === undefined) {
            return documents;
        }
        if (folderFilter === NO_FOLDER) {
            return documents.filter(d => !d.folder);
        }
        return documents.filter(d => d.folder === folderFilter);
    }, [documents, folderFilter]);

    const doUpload = async (file: File, overwrite: boolean, folder?: string) => {
        setUploading(true);
        try {
            await DocumentService.Upload(file, overwrite, folder);
            message.success(GetText('Documents_UploadSuccess') || 'Dokumentet har laddats upp.');
            await loadDocuments();
        } catch (error) {
            if (error instanceof ResponseError && error.response.status === 409) {
                Modal.confirm({
                    title: GetText('Documents_OverwriteTitle') || 'Filen finns redan',
                    content: GetText('Documents_OverwriteContent') || `En fil med namnet "${file.name}" finns redan. Vill du skriva över den?`,
                    okText: GetText('Documents_OverwriteConfirm') || 'Skriv över',
                    cancelText: GetText('Common_Cancel') || 'Avbryt',
                    onOk: () => doUpload(file, true, folder),
                });
            } else if (error instanceof ResponseError) {
                const text = await error.response.text();
                message.error(text || GetText('Documents_UploadFailed') || 'Kunde inte ladda upp filen.');
            } else {
                message.error(GetText('Documents_UploadFailed') || 'Kunde inte ladda upp filen.');
            }
        }
        setUploading(false);
    };

    const handleBeforeUpload = (file: File) => {
        if (file.size > MAX_FILE_SIZE_BYTES) {
            message.error(GetText('Documents_FileTooLarge') || 'Filen är för stor. Max 25 MB per fil.');
            return Upload.LIST_IGNORE;
        }

        if (!ALLOWED_CONTENT_TYPES.includes(file.type)) {
            message.error(GetText('Documents_FileTypeNotAllowed') || 'Filtypen stöds inte.');
            return Upload.LIST_IGNORE;
        }

        // Prefill the folder with the one currently being filtered on, if any.
        const prefill = folderFilter && folderFilter !== NO_FOLDER ? folderFilter : '';
        setFolderModal({ open: true, mode: 'upload', file, value: prefill });
        return false;
    };

    const handleFolderModalOk = async () => {
        if (!folderModal.open) {
            return;
        }
        const folder = folderModal.value.trim() || undefined;

        if (folderModal.mode === 'upload') {
            const file = folderModal.file;
            setFolderModal({ open: false });
            await doUpload(file, false, folder);
            return;
        }

        const docId = folderModal.docId;
        setFolderModal({ open: false });
        try {
            await DocumentService.UpdateFolder(docId, folder);
            message.success(GetText('Documents_MoveSuccess') || 'Dokumentet har flyttats.');
            await loadDocuments();
        } catch (error) {
            if (error instanceof ResponseError) {
                const text = await error.response.text();
                message.error(text || GetText('Documents_MoveFailed') || 'Kunde inte flytta dokumentet.');
            } else {
                message.error(GetText('Documents_MoveFailed') || 'Kunde inte flytta dokumentet.');
            }
        }
    };

    const handleDelete = async (id?: number) => {
        if (!id) {
            return;
        }
        try {
            await DocumentService.Delete(id);
            message.success(GetText('Documents_DeleteSuccess') || 'Dokumentet har tagits bort.');
            await loadDocuments();
        } catch {
            message.error(GetText('Documents_DeleteFailed') || 'Kunde inte ta bort dokumentet.');
        }
    };

    const handleDownload = async (doc: WebApiDocumentsDtoCompanyDocumentDto) => {
        if (!doc.id) {
            return;
        }
        try {
            const response = await DocumentService.Download(doc.id);
            const blob = await response.raw.blob();
            const fileName = getFileNameFromContentDisposition(response.raw.headers.get('content-disposition'), doc.fileName || 'document');
            downloadBlobFile(blob, fileName);
        } catch {
            message.error(GetText('Documents_DownloadFailed') || 'Kunde inte ladda ner filen.');
        }
    };

    const columns: ColumnsType<WebApiDocumentsDtoCompanyDocumentDto> = [
        {
            title: GetText('Documents_ColumnName') || 'Namn',
            dataIndex: 'fileName',
            key: 'fileName',
            sorter: (a, b) => (a.fileName || '').localeCompare(b.fileName || ''),
        },
        {
            title: GetText('Documents_ColumnFolder') || 'Mapp',
            dataIndex: 'folder',
            key: 'folder',
            render: (folder?: string) => folder || <span style={{ color: '#bbb' }}>{GetText('Documents_FolderNone') || 'Osorterat'}</span>,
            sorter: (a, b) => (a.folder || '').localeCompare(b.folder || ''),
            defaultSortOrder: 'ascend',
        },
        {
            title: GetText('Documents_ColumnSize') || 'Storlek',
            dataIndex: 'sizeBytes',
            key: 'sizeBytes',
            render: (sizeBytes: number) => formatBytes(sizeBytes),
            sorter: (a, b) => (a.sizeBytes || 0) - (b.sizeBytes || 0),
        },
        {
            title: GetText('Documents_ColumnUploadedBy') || 'Uppladdad av',
            dataIndex: 'createdByName',
            key: 'createdByName',
            sorter: (a, b) => (a.createdByName || '').localeCompare(b.createdByName || ''),
        },
        {
            title: GetText('Documents_ColumnUploadedAt') || 'Datum',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (createdAt: string) => createdAt ? formatDateString(createdAt) : '',
            sorter: (a, b) => new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime(),
        },
        {
            title: GetText('MemberList_ActionColumn') || 'Åtgärder',
            key: 'actions',
            render: (_, doc) => (
                <Space size="middle">
                    <DownloadOutlined style={{ fontSize: '18px', cursor: 'pointer' }} onClick={() => handleDownload(doc)} />
                    {!user?.hasOnlyReadRights && (
                        <FolderOpenOutlined
                            style={{ fontSize: '18px', cursor: 'pointer' }}
                            title={GetText('Documents_Move') || 'Flytta'}
                            onClick={() => doc.id && setFolderModal({ open: true, mode: 'move', docId: doc.id, value: doc.folder || '' })}
                        />
                    )}
                    {!user?.hasOnlyReadRights && (
                        <Popconfirm
                            title={GetText('Common_ConfirmDelete') || 'Är du säker på att du vill ta bort filen?'}
                            onConfirm={() => handleDelete(doc.id)}
                            okText={GetText('Common_Yes') || 'Ja'}
                            cancelText={GetText('Common_No') || 'Nej'}
                        >
                            <DeleteOutlined style={{ fontSize: '18px', color: 'red', cursor: 'pointer' }} />
                        </Popconfirm>
                    )}
                </Space>
            ),
        },
    ];

    const usagePercent = limitBytes > 0 ? Math.min(100, Math.round((usedBytes / limitBytes) * 100)) : 100;
    const hasDocumentsAccess = user?.subscriptionLevel === SubscriptionLevel.Premium || user?.subscriptionLevel === SubscriptionLevel.Enterprise;

    return (
        <LayoutLeftMenu menu={<LeftMenuSettings selected="documents" />}>
            <Title level={2}>{GetText('TopHeader_Documents') || 'Dokument'}</Title>
            <p>{GetText('Documents_Description') || 'Dokument som endast är synliga och åtkomliga för administratörer i er förening.'}</p>

            {hasDocumentsAccess ? (
                <div style={{ maxWidth: 300, marginBottom: 20 }}>
                    <span style={{ fontSize: '12px', color: '#888' }}>{formatBytes(usedBytes)} / {formatBytes(limitBytes)}</span>
                    <Progress
                        percent={usagePercent}
                        size="small"
                        showInfo={false}
                        status={usagePercent >= 100 ? 'exception' : 'normal'}
                        strokeColor={usagePercent >= 100 ? undefined : '#bfbfbf'}
                    />
                </div>
            ) : (
                <p><i>{GetText('Documents_NotAvailableForSubscription') || 'Dokumentfunktionen ingår inte i er nuvarande abonnemangsnivå.'}</i></p>
            )}

            <Space wrap style={{ marginBottom: 8 }}>
                {!user?.hasOnlyReadRights && hasDocumentsAccess && (
                    <Upload
                        showUploadList={false}
                        beforeUpload={handleBeforeUpload}
                        disabled={uploading}
                    >
                        <Button icon={<UploadOutlined />} loading={uploading}>
                            {GetText('Documents_UploadHint') || 'Ladda upp dokument'}
                        </Button>
                    </Upload>
                )}

                {existingFolders.length > 0 && (
                    <Select
                        allowClear
                        style={{ minWidth: 200 }}
                        placeholder={GetText('Documents_FolderAll') || 'Alla mappar'}
                        value={folderFilter}
                        onChange={value => setFolderFilter(value)}
                        options={[
                            { value: NO_FOLDER, label: GetText('Documents_FolderNone') || 'Osorterat' },
                            ...existingFolders.map(f => ({ value: f, label: f })),
                        ]}
                    />
                )}
            </Space>

            <Table
                style={{ marginTop: 16 }}
                columns={columns}
                dataSource={filteredDocuments}
                rowKey="id"
                loading={loading}
                pagination={false}
            />

            <Modal
                open={folderModal.open}
                title={folderModal.open && folderModal.mode === 'move'
                    ? (GetText('Documents_MoveModalTitle') || 'Flytta till mapp')
                    : (GetText('Documents_UploadModalTitle') || 'Ladda upp dokument')}
                okButtonProps={{ loading: uploading }}
                onOk={handleFolderModalOk}
                onCancel={() => setFolderModal({ open: false })}
                cancelText={GetText('Common_Cancel') || 'Avbryt'}
                destroyOnClose
            >
                <label style={{ display: 'block', marginBottom: 6 }}>{GetText('Documents_ChooseFolder') || 'Mapp (valfritt)'}</label>
                <AutoComplete
                    style={{ width: '100%' }}
                    allowClear
                    options={folderOptions}
                    value={folderModal.open ? folderModal.value : ''}
                    placeholder={GetText('Documents_ChooseFolderPlaceholder') || 'Välj eller skriv en mapp'}
                    filterOption={(input, option) => (option?.value ?? '').toLowerCase().includes(input.toLowerCase())}
                    onChange={value => setFolderModal(prev => (prev.open ? { ...prev, value: value ?? '' } : prev))}
                />
            </Modal>
        </LayoutLeftMenu>
    );
};

export default Documents;
